import React, {useState, useEffect} from 'react';
import Fab from '@material-ui/core/Fab';
import theme from '../../../theme';
import CloseButton from '../../map2/CloseButton';
import Tooltip from '@material-ui/core/Tooltip';
import GettingStartedContent from './GettingStartedContent';
import {getUserOnboardingChecklist} from './getUserOnboardingChecklist';
import {getLocalStorageItem, setLocalStorageItem} from '../../../utils/localStorage';
import trackAnalytics from '../../../utils/gaia/trackAnalytics';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {isGettingStartedExperimentDisplayed} from './isGettingStartedExperimentDisplayed';

export interface GettingStartedFABState {
    isFABTemporarilyDismissed: boolean;
    isFABPermanentlyDismissed: boolean;
    isGSContentOpen: boolean;
    isFABCloseButtonHidden: boolean;
}

export default function GettingStartedFAB({user}: {user: User | undefined}) {
    const localStorageKey = 'getting_started_module';
    const isExperimentDisplayed = isGettingStartedExperimentDisplayed(user);
    const isMinScreenSize = useMediaQuery(theme.breakpoints.up('sm'));
    const userOnboardingChecklist = getUserOnboardingChecklist(user);
    const checklistCompleteCount = userOnboardingChecklist?.filter(item => item.complete).length;
    const referrer = document?.referrer ? new URL(document.referrer) : undefined;
    const isGSContentAutoOpen = (referrer && (referrer.origin !== window.location.origin ||
            referrer.pathname.includes('/register/') ||
            referrer.pathname.includes('/login/') ||
            referrer.pathname === '/'));

    const [gettingStarted, setGettingStarted] = useState<GettingStartedFABState>({
        isFABTemporarilyDismissed: false,
        isFABPermanentlyDismissed: getLocalStorageItem(localStorageKey, false),
        isGSContentOpen: isGSContentAutoOpen || false,
        isFABCloseButtonHidden: true,
    });

    useEffect(() =>
        setLocalStorageItem(
            localStorageKey,
            gettingStarted.isFABPermanentlyDismissed
        )
    , [gettingStarted.isFABPermanentlyDismissed, localStorageKey]);

    if (user?.isAuthenticated &&
        userOnboardingChecklist?.length === checklistCompleteCount &&
        !gettingStarted.isFABPermanentlyDismissed
    ) {
        setGettingStarted(state=> ({
            ...state,
            isFABPermanentlyDismissed: true,
        }));
    }

    if (!user ||
        !user.isAuthenticated ||
        gettingStarted.isFABPermanentlyDismissed ||
        gettingStarted.isFABTemporarilyDismissed ||
        !isExperimentDisplayed
    ) return null;

    return (<>
        <Tooltip
            title={<GettingStartedContent
                setGettingStarted={setGettingStarted}
                gettingStarted={gettingStarted}
                userOnboardingChecklist={userOnboardingChecklist}
            />}
            placement='top-end'
            interactive
            PopperProps={{
                disablePortal: true
            }}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            open={gettingStarted.isGSContentOpen}
            onClose={()=> setGettingStarted({
                ...gettingStarted,
                isGSContentOpen: false
            })}
        >
            <Fab
                style={{
                    fontSize: 14,
                    position: 'fixed',
                    // attribution box is 20px tall
                    bottom: 20 + theme.spacing(1),
                    right: theme.spacing(2),
                    ...(isMinScreenSize ? {paddingRight: theme.spacing(3)} : {})
                }}
                variant='extended'
                color='primary'
                onMouseEnter={() =>
                    setGettingStarted({
                        ...gettingStarted,
                        isFABCloseButtonHidden: gettingStarted.isGSContentOpen,
                    })
                }
                onMouseLeave={() =>
                    setGettingStarted({
                        ...gettingStarted,
                        isFABCloseButtonHidden: true,
                    })
                }
                onClick={() => {
                    setGettingStarted({
                        ...gettingStarted,
                        isFABCloseButtonHidden: true,
                        isGSContentOpen: !gettingStarted.isGSContentOpen,
                    });
                    trackAnalytics('Clicked Getting Started', {
                        name: 'Open Checklist',
                    });
                }}
            >
                <span style={isMinScreenSize ? {marginRight: theme.spacing(1)} : {}}>
                    {checklistCompleteCount}/{userOnboardingChecklist?.length}
                </span>
                {isMinScreenSize && 'Getting Started'}
            </Fab>
        </Tooltip>
        {!gettingStarted.isFABCloseButtonHidden && (
            <CloseButton
                style={{
                    position: 'fixed',
                    bottom: 20 + theme.spacing(5),
                    right: theme.spacing(1),
                }}
                onMouseEnter={() =>
                    setGettingStarted({
                        ...gettingStarted,
                        isFABCloseButtonHidden: false,
                    })
                }
                onMouseLeave={() =>
                    setGettingStarted({
                        ...gettingStarted,
                        isFABCloseButtonHidden: true,
                    })
                }
                onClick={() => {
                    setGettingStarted({
                        ...gettingStarted,
                        isFABTemporarilyDismissed: true,
                    });
                    trackAnalytics('Clicked Getting Started', {
                        name: 'Hide button',
                    });
                }}
            />
        )}
    </>);
}
